import { useGetGames } from "@/services/games";
import type { VideoGame } from "@/interfaces/api";
import { useEffect, useState } from "react";
import { useDebounceValue } from "usehooks-ts";
import VideoGameListItem from "./VideoGameListItem";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/shadcn/dialog";
import { Button } from "@/components/shadcn/Button";
import { Input } from "./shadcn/input";
import Spinner from "./ui/Spinner";
import { cn } from "@/lib/utils";

interface GuessDialogProps {
  showModal: boolean;
  tries: number;
  gotWrong: boolean;
  dialogLabel: string;
  handleClose: () => void;
  handleGuess: (guess: VideoGame) => void;
}

export default function GuessDialog(props: GuessDialogProps) {
  const { handleClose, showModal, tries, handleGuess, gotWrong, dialogLabel } =
    props;

  const [focused, setFocused] = useState(false);
  const [guess, setGuess] = useState<VideoGame>();
  const [searchTerm, setSearchTerm] = useState("");

  const { result, searchGames, fetching } = useGetGames();

  const [debouncedSearchTerm] = useDebounceValue(searchTerm, 1000);

  useEffect(() => {
    if (debouncedSearchTerm.length === 0) return;
    searchGames(debouncedSearchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  const onClose = () => {
    handleClose();
    setSearchTerm("");
    setGuess(undefined);
  };

  const onGuess = (guess: VideoGame) => {
    handleGuess(guess);
    setGuess(undefined);
    setSearchTerm("");
  };

  return (
    <Dialog open={showModal} onOpenChange={onClose}>
      <DialogContent className="grid gap-2">
        <DialogHeader className="grid text-white">
          <DialogTitle>{dialogLabel}</DialogTitle>
          <DialogDescription
            className={cn("mt-2", {
              "transition animate-pulse text-red-500": gotWrong,
            })}
          >
            Tries left: {tries}
          </DialogDescription>
        </DialogHeader>
        <Input
          data-testid="search-game"
          value={guess?.name || searchTerm}
          className="text-white color-white"
          placeholder="Search game"
          onChange={(e) => {
            setSearchTerm(e.target.value);
          }}
          onFocus={() => {
            setFocused(true);
          }}
          onBlur={() => {
            setTimeout(() => {
              setFocused(false);
            }, 200);
          }}
        />
        {fetching && (
          <div className="flex gap-2 items-center animate-pulse text-white">
            <Spinner className="text-deep-purple-700" />
            <h6>Now Loading...</h6>
          </div>
        )}
        {result && !fetching && searchTerm.length > 0 && focused && (
          <ul className="text-white overflow-auto overflow-x-hidden max-h-[300px]">
            {result.length > 0 &&
              result.map((game) => (
                <VideoGameListItem
                  key={game.id}
                  game={game}
                  onClick={() => setGuess(game)}
                />
              ))}
            {result.length === 0 && (
              <p className="text-white">No results found</p>
            )}
          </ul>
        )}
        <Button
          className="mt-4"
          disabled={!guess}
          onClick={() => {
            onGuess(guess!);
          }}
        >
          Guess
        </Button>
      </DialogContent>
    </Dialog>
  );
}
